<template>
<div>
    <form v-if="!sponsorAdded" @submit.prevent="addSponsor()" class="field">
        <h2>{{ $t("addSponsorView.fillSponsorInformation") }}</h2>
        <div class="radio-content">
            <label for="privateSponsor">
                <input type="radio" name="sponsorType" id="privateSponsor" checked value="Private" v-model="sponsor.sponsorType" /> 
                <span class="label-text">{{ $t("addSponsorView.private") }}</span>
                <span class="checkmark icon-checkmark"></span>
            </label>
            <label for="companySponsor">
                <input type="radio" name="sponsorType" id="companySponsor" value="Company" v-model="sponsor.sponsorType" /> 
                <span class="label-text">{{ $t("addSponsorView.company") }}</span>
                <span class="checkmark icon-checkmark"></span>
            </label>
        </div>
        <input v-if="sponsor.sponsorType === 'Company'" type="text" v-model="sponsor.companyName" :placeholder="$t('addSponsorView.companyName')" />

        <input required type="text" :placeholder="$t('addSponsorView.firstname')" v-model="sponsor.firstname" />
        <input required type="text" :placeholder="$t('addSponsorView.lastname')" v-model="sponsor.lastname" />
        <!-- TODO: email required? -->
        <input type="email" :placeholder="$t('addSponsorView.email')" v-model="sponsor.email" />
        <input type="text" :placeholder="$t('addSponsorView.streetAddress')" v-model="sponsor.streetAddress" />
        <input type="text" :placeholder="$t('addSponsorView.zipCode')" v-model="sponsor.zipCode" @change="zipCodeChanged()" />
        <input type="text" :placeholder="$t('addSponsorView.city')" v-model="sponsor.city" />
        <input v-if="sponsor.sponsorType == 'Company'" type="text" :placeholder="$t('addSponsorView.info')" v-model="sponsor.info" />

        <div class="radio-content">
            <label for="oneTimePayment">
                <input type="radio" name="paymentType" id="oneTimePayment" checked value="Manual" v-model="sponsor.paymentMethod" />
                <span class="label-text">{{ $t("addSponsorView.oneTimePayment") }}</span>
                <span class="checkmark icon-checkmark"></span>
            </label>
            <label for="paymentPerRepeat">
                <input type="radio" name="paymentType" id="paymentPerRepeat" value="PerRepeat" v-model="sponsor.paymentMethod" @change="perRepeatPaymentSelected" />
                <span class="label-text">{{ $t("addSponsorView.paymentPerRepeat") }}</span>
                <span class="checkmark icon-checkmark"></span>
            </label>
        </div>

        <input required type="number" step="any" :placeholder="$t('addSponsorView.paymentAmount', [minPayment])" v-model="sponsor.payment" />

        <div class="radio-content">
            <label for="paymentMethodMail">
                <input
                    id="paymentMethodMail"
                    type="radio"
                    name="payment-method"
                    value="Mail"
                    v-model="sponsor.preferredBillingMethod"
                    @change="billingMethodChanged()"
                />
                <span class="label-text">{{ $t("addSponsorView.paymentMethodMail") }}</span>
                <span class="checkmark icon-checkmark"></span>
            </label>

            <label for="paymentMethodEmail">
                <input
                    id="paymentMethodEmail"
                    type="radio"
                    name="payment-method"
                    value="Email"
                    v-model="sponsor.preferredBillingMethod"
                    @change="billingMethodChanged()"
                />
                <span class="label-text">{{ $t("addSponsorView.paymentMethodEmail") }}</span>
                <span class="checkmark icon-checkmark"></span>
            </label>

        </div>

        <button type="submit" class="button button-green" :disabled="!isValidatedForSave">
            {{ $t("addSponsorView.saveSponsor") }}
        </button>
     </form>
     <div v-else>
         <h2>{{ $t("addSponsorView.sponsorAdded") }}</h2>
     </div>
</div>
</template>

<script lang="ts">
import { store, Sponsor, PaymentType } from "store";
import axios from "axios";
import { defineComponent } from "vue";

export default defineComponent({
    async mounted() {
        this.setTitle();
        this.sponsorAdded = false;
    },
    data() {
        const sponsor: Sponsor = {
            sponsorId: null,
            firstname: null,
            lastname: null,
            fullName: null,
            email: null,
            sponsorType: "Private",
            companyName: null,
            payment: null,
            paymentMethod: "Manual",
            billSentOn: null,
            participationStatus: "Participates",
            notificationSentOn: null,
            numNotificationsSent: 0,
            shouldPay: null,
            paid: 0,
            estimatedPayment: null,
            eventParticipantId: store.selectedEvent.eventParticipant.eventParticipantId,
            streetAddress: "",
            zipCode: "",
            city: "",
            sendingEmail: false,
            editingEmail: false,
            info: null,
            preferredBillingMethod: "Email",
        };

        return {
            sponsorAdded: false,
            sponsor,
            store: store
        };
    },
    computed: {
        minPayment() {
            if (this.sponsor.paymentMethod == "Manual") {
                return store.globalSettings.paymentMinManual;
            }

            return store.globalSettings.paymentMinPerRepeat;
        },
        isValidatedForSave() {
            if (!this.sponsor.payment || this.sponsor.payment < this.minPayment) {
                return false;
            }

           let requiredFields = [];
           if (this.sponsor.sponsorType === "Private") {
               requiredFields = [
                   this.sponsor.firstname,
                   this.sponsor.lastname,
               ];
           }
           if (this.sponsor.sponsorType === "Company") {
               requiredFields = [ this.sponsor.companyName ];
           }

           switch (this.sponsor.preferredBillingMethod) {
               case "Mail":
                   requiredFields.push(this.sponsor.streetAddress, this.sponsor.city, this.sponsor.zipCode);
                   break;
               case "Email":
                   requiredFields.push(this.sponsor.email);
                   break;

           }

           return this.validateTextInputs(requiredFields);
        },
        isDuplicateSponsor() {
            if (this.sponsor.email === null) {
                return false;
            }

            return store.selectedEvent.sponsors.find(s => s.email === this.sponsor.email && s.sponsorType === this.sponsor.sponsorType) != null;
        }
    },
    methods: {
        setTitle() {
            store.page.title = this.$t("addSponsorView.title") as string;
            store.page.showTopNav = true;
        },
        billingMethodChanged() {
        },
        validateTextInputs(fields: string[]): boolean {
            const isValidTextInput = (s?: string) => s && s.trim().length > 0;

            return fields
                .map(isValidTextInput)
                .every(x => x === true);
        },
        async addSponsor() {
            try {
                let sponsorId = (await axios.post<Sponsor>("sponsors", this.sponsor,
                    { params: { eventId: store.selectedEvent.eventId } })).data.sponsorId;

                let newSponsor = (await axios.get<Sponsor>(`sponsors/${sponsorId}`)).data;
                store.selectedEvent.sponsors.push(newSponsor);
                this.sponsorAdded = true;
                this.$router.push("/sponsors?sponsorAdded");
            } catch(e) {
                // TODO: Handle error
            }
        },
        perRepeatPaymentSelected(e) {
            let value = e.target.value as PaymentType;
            if (value === "PerRepeat" && this.sponsor.payment)
                this.sponsor.payment = 0;
        },
        async zipCodeChanged() {
            let http = axios.create({ 
                baseURL: "https://locationservice.posti.com",
            });
            let { locations } = (await http.get(`location?zipCode=${this.sponsor.zipCode}&types=POBOX&types=PICKUPPOINT&types=POSTOFFICE`)).data;
            (locations as any[]).sort((a, b) => {
                if (a.type == "POSTOFFICE" || a.type == "PICKUPPOINT") {
                  return -1;
                }

                return 1;
            });
            let cityName = "";
            let language = this.$i18n.locale == "se" ? "sv" : this.$i18n.locale;

            for (var l of locations) {
                var postalCodeName = l.address[language].postalCodeName;
                var municipality = l.address[language].municipality;

                if (postalCodeName) {
                    cityName = postalCodeName;
                    break;
                }

                else if (municipality) {
                    cityName = municipality;
                }
            }
            this.sponsor.city = cityName;
        }
    }
})

</script>